// ===========================================
// Revenue by Term Chart Component
// ===========================================
// Bar chart comparing revenue across terms

import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { RevenueByTerm } from '../../api/reports.api';
import { formatCurrency } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

// Revenue BY TERM, from getRevenueReport's `byTerm` (last 5 terms). This
// component used to be typed against the month-shaped MRR trend data, which
// the revenue report never returns.
interface RevenueByTermChartProps {
  data: RevenueByTerm[];
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function RevenueByTermChart({ data, loading = false }: RevenueByTermChartProps) {
  if (loading || !data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Revenue by Term
          </Typography>
          <Box
            sx={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading...' : 'No data available'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Transform data for Recharts (convert cents to dollars).
  // The backend already limits this to the last 5 terms (getRevenueByTerm).
  const chartData = data.map((item) => ({
    name: item.termName,
    revenue: item.totalRevenue / 100,
    invoiceCount: item.invoiceCount,
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Revenue by Term (Last 5 Terms)
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tickFormatter={(value) => `$${value.toLocaleString()}`}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: any, name: any) => {
                if (value === undefined || value === null) return ['N/A', name || ''];
                if (name === 'revenue') {
                  return [formatCurrency(Math.round(value * 100)), 'Revenue'];
                }
                return [value, 'Invoices'];
              }}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
            <Legend />
            <Bar dataKey="revenue" fill="#4580E4" name="Revenue" />
            <Bar dataKey="invoiceCount" fill="#FFCE00" name="Invoices" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
